import { Add, DeleteOutline } from '@mui/icons-material';
import { Box, IconButton } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { validDepartmentNamePattern } from '../../../constants/agentConstants';
import { AnalyticsEvents } from '../../../enums/AnalyticsEvents';
import { useAnalytics } from '../../../hooks/useAnalytics';
import { useTranslation } from '../../../hooks/useTranslation';
import { AgentCapability } from '../../../interfaces/Agent';
import StyledFormItem from '../../../styles/StyledFormItem';
import Button from '../../shared/Button/Button';
import Input from '../../shared/Input/Input';
import TelephoneInput from '../../shared/TelephoneInput/TelephoneInput';
import Text from '../../shared/Text/Text';
import ChooseCapability from './ChooseCapability';

interface TransferCallsError {
  department_name?: {
    message?: string;
    type?: string;
  };
}

interface FormErrors {
  transfer_calls?: TransferCallsError[];
}

interface TransferCallsCapabilityProps {
  maxTransferCalls: number;
  usedAgentPhoneNumbers?: (string | undefined)[];
}

/**
 * @deprecated by TransferCallsCapabilityV1
 */
const TransferCallsCapability: React.FC<TransferCallsCapabilityProps> = ({
  maxTransferCalls,
  usedAgentPhoneNumbers = [],
}) => {
  const {
    setValue,
    control,
    formState: { errors },
    watch,
  } = useFormContext();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'transfer_calls',
  });

  const { track } = useAnalytics();
  const { t } = useTranslation();

  const formErrors = errors as unknown as FormErrors;

  const selectedCapability = watch('external_capabilities', '');
  const transferCallsData = watch('transfer_calls') || [];

  const shouldDisplayTransferCalls =
    selectedCapability === 'transfer_calls' ||
    (Array.isArray(selectedCapability) &&
      selectedCapability.includes('transfer_calls')) ||
    fields.length > 0;

  const handleAddTransferCall = useCallback(() => {
    if (fields.length < maxTransferCalls) {
      append({ department_name: '', phone_number: '+352' });
      track(AnalyticsEvents.AGENT_TRANSFER_CALLS_CONFIGURED, {
        action: 'add',
        count: fields.length + 1,
      });
    }
  }, [fields.length, maxTransferCalls, append, track]);

  const handleRemoveTransferCall = useCallback(
    (index: number) => {
      remove(index);
      track(AnalyticsEvents.AGENT_TRANSFER_CALLS_CONFIGURED, {
        action: 'remove',
        count: fields.length - 1,
      });

      // If this was the last item, clear the capability selection
      if (fields.length === 1) {
        setValue('external_capabilities', '');
        setValue('transfer_calls', []);
      }
    },
    [remove, track, fields.length, setValue]
  );

  const handleDepartmentChange = useCallback(
    (index: number, value: string) => {
      setValue(`transfer_calls.${index}.department_name`, value);
      track(AnalyticsEvents.AGENT_DEPARTMENT_SELECTED, {
        department: value,
        index,
      });
    },
    [setValue, track]
  );

  useEffect(() => {
    if (shouldDisplayTransferCalls && fields.length === 0) {
      // Only add initial item if the capability is explicitly selected
      if (
        selectedCapability === 'transfer_calls' ||
        (Array.isArray(selectedCapability) &&
          selectedCapability.includes('transfer_calls'))
      ) {
        if (transferCallsData.length > 0) {
          transferCallsData.forEach(
            (transferCall: {
              department_name: string;
              phone_number: string;
            }) => {
              append(transferCall);
            }
          );
        } else {
          append({ department_name: '', phone_number: '+352' });
        }
      }
    }
  }, [
    shouldDisplayTransferCalls,
    fields.length,
    append,
    transferCallsData,
    selectedCapability,
  ]);

  useEffect(() => {
    if (!shouldDisplayTransferCalls) {
      setValue('external_capabilities', '');
      setValue('transfer_calls', []);
    }
  }, [shouldDisplayTransferCalls, setValue]);

  useEffect(() => {
    if (fields.length === 0 && !shouldDisplayTransferCalls) {
      setValue('external_capabilities', '');
    }
  }, [fields.length, shouldDisplayTransferCalls, setValue]);

  return (
    <>
      <ChooseCapability
        capabilities={[
          {
            label: t('Transfer calls'),
            value: AgentCapability.TRANSFER_CALLS,
          },
        ]}
      />

      {shouldDisplayTransferCalls && (
        <>
          {fields.map((field, index: number) => (
            <Box
              sx={{
                display: 'flex',
                gap: '1rem',
                alignItems: 'flex-start',
                position: 'relative',
                justifyContent: 'space-between',
              }}
              key={field.id}
            >
              <StyledFormItem flexGrow={1}>
                <Text type="label">{`Department`}</Text>
                <Input
                  name={`transfer_calls.${index}.department_name`}
                  required
                  maxLength={50}
                  withError={
                    !!formErrors.transfer_calls?.[index]?.department_name
                  }
                  helperText={
                    formErrors.transfer_calls?.[index]?.department_name
                      ?.message ||
                    (!!formErrors.transfer_calls?.[index]?.department_name
                      ? 'Department name is required'
                      : '')
                  }
                  onChange={event =>
                    handleDepartmentChange(index, event.target.value)
                  }
                  pattern={validDepartmentNamePattern}
                />
              </StyledFormItem>
              <TelephoneInput
                name={`transfer_calls.${index}.phone_number`}
                required
                usedPhoneNumbers={usedAgentPhoneNumbers}
                usedPhoneNumbersErrorText="This phone number is currently attached to an agent"
              />
              <IconButton
                color="error"
                sx={{
                  height: '50px',
                  width: '50px',
                  marginTop: '50px',
                  backgroundColor: 'var(--IconButton-hoverBg)',
                  position: 'absolute',
                  right: '-82px',
                }}
                type="button"
                onClick={() => handleRemoveTransferCall(index)}
              >
                <DeleteOutline />
              </IconButton>
            </Box>
          ))}

          <Box sx={{ marginTop: '1rem' }}>
            <Button
              type="button"
              variant="outlined"
              color="inherit"
              size="small"
              onClick={handleAddTransferCall}
              disabled={fields.length >= maxTransferCalls}
            >
              <Add sx={{ marginRight: '0.5rem' }} />
              {t('Add')}
            </Button>
          </Box>
        </>
      )}
    </>
  );
};

export default TransferCallsCapability;
